import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ArrowLeftRight,
  TrendingUp,
  TrendingDown,
  Minus,
  Eye,
  Info,
  Lock,
} from 'lucide-react';
import type { BudgetRevision, CostCategoryType, RevisionRoleSnapshot, RevisionCategorySnapshot } from '@/types/budget.types';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RoleCompareRow {
  roleId: number;
  role: string;
  revAHours: number | null;
  revBHours: number | null;
  deltaHours: number;
  deltaPercent: number;
  changeType: 'added' | 'removed' | 'increased' | 'decreased' | 'unchanged';
}

interface CategoryCompareRow {
  categoryId: number;
  categoryName: string;
  type?: CostCategoryType;
  revABudget: number | null;
  revBBudget: number | null;
  deltaBudget: number;
  deltaPercent: number;
  changeType: 'added' | 'removed' | 'increased' | 'decreased' | 'unchanged';
}

const CAT_TYPE_ORDER: CostCategoryType[] = ['labour', 'expenses', 'subcontractors'];
const CAT_TYPE_LABELS: Record<CostCategoryType, string> = {
  labour: 'Labour',
  expenses: 'Expenses',
  subcontractors: 'Subcontractors',
  materials: 'Materials',
  equipment: 'Equipment',
  overhead: 'Overhead',
  contingency: 'Contingency',
};

interface RevisionCompareViewProps {
  revisionA: BudgetRevision;
  revisionB: BudgetRevision;
  /** Controls column headers (h vs £), data source, and footer note. Defaults to 'resource'. */
  budgetLayer?: 'resource' | 'cost' | 'sales';
  onViewRevA?: () => void;
  onViewRevB?: () => void;
  onSwap?: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function buildCompareRows(
  snapshotA: RevisionRoleSnapshot[],
  snapshotB: RevisionRoleSnapshot[]
): RoleCompareRow[] {
  const allRoleIds = new Set([
    ...snapshotA.map((r) => r.roleId),
    ...snapshotB.map((r) => r.roleId),
  ]);

  const rows: RoleCompareRow[] = [];

  allRoleIds.forEach((roleId) => {
    const a = snapshotA.find((r) => r.roleId === roleId);
    const b = snapshotB.find((r) => r.roleId === roleId);

    const revAHours = a?.budgetHours ?? null;
    const revBHours = b?.budgetHours ?? null;
    const delta = (revAHours ?? 0) - (revBHours ?? 0);
    const base = revBHours ?? revAHours ?? 0;
    const deltaPercent = base !== 0 ? Math.round((delta / base) * 100) : 0;

    let changeType: RoleCompareRow['changeType'] = 'unchanged';
    if (!b) changeType = 'added';
    else if (!a) changeType = 'removed';
    else if (delta > 0) changeType = 'increased';
    else if (delta < 0) changeType = 'decreased';

    rows.push({
      roleId,
      role: a?.role ?? b?.role ?? 'Unknown Role',
      revAHours,
      revBHours,
      deltaHours: delta,
      deltaPercent,
      changeType,
    });
  });

  const order = { added: 0, removed: 1, increased: 2, decreased: 3, unchanged: 4 };
  return rows.sort((a, b) => order[a.changeType] - order[b.changeType]);
}

function buildCategoryCompareRows(
  snapshotA: RevisionCategorySnapshot[],
  snapshotB: RevisionCategorySnapshot[]
): CategoryCompareRow[] {
  const allIds = new Set([
    ...snapshotA.map((c) => c.categoryId),
    ...snapshotB.map((c) => c.categoryId),
  ]);

  const rawRows: CategoryCompareRow[] = [];

  allIds.forEach((categoryId) => {
    const a = snapshotA.find((c) => c.categoryId === categoryId);
    const b = snapshotB.find((c) => c.categoryId === categoryId);

    const revABudget = a?.budget ?? null;
    const revBBudget = b?.budget ?? null;
    const delta = (revABudget ?? 0) - (revBBudget ?? 0);
    const base = revBBudget ?? revABudget ?? 0;
    const deltaPercent = base !== 0 ? Math.round((delta / base) * 100) : 0;

    let changeType: CategoryCompareRow['changeType'] = 'unchanged';
    if (!b) changeType = 'added';
    else if (!a) changeType = 'removed';
    else if (delta > 0) changeType = 'increased';
    else if (delta < 0) changeType = 'decreased';

    rawRows.push({
      categoryId,
      categoryName: a?.categoryName ?? b?.categoryName ?? 'Unknown Category',
      type: a?.type ?? b?.type,
      revABudget,
      revBBudget,
      deltaBudget: delta,
      deltaPercent,
      changeType,
    });
  });

  // Aggregate all labour-type rows into a single synthetic "Labour" row, matching
  // the edit screen's locked Labour group row.
  const labourRows = rawRows.filter((r) => r.type === 'labour');
  const nonLabourRows = rawRows.filter((r) => r.type !== 'labour');

  const aggregated: CategoryCompareRow[] = [...nonLabourRows];

  if (labourRows.length > 0) {
    const revA = labourRows.reduce((s, r) => s + (r.revABudget ?? 0), 0);
    const revB = labourRows.reduce((s, r) => s + (r.revBBudget ?? 0), 0);
    const delta = revA - revB;
    const base = revB !== 0 ? revB : revA;
    const deltaPercent = base !== 0 ? Math.round((delta / base) * 100) : 0;
    let changeType: CategoryCompareRow['changeType'] = 'unchanged';
    if (delta > 0) changeType = 'increased';
    else if (delta < 0) changeType = 'decreased';

    aggregated.unshift({
      categoryId: -1,
      categoryName: 'Labour',
      type: 'labour',
      revABudget: revA,
      revBBudget: revB,
      deltaBudget: delta,
      deltaPercent,
      changeType,
    });
  }

  // Sort: Labour first (synthetic, id -1), then non-labour by type order then change severity
  const order = { added: 0, removed: 1, increased: 2, decreased: 3, unchanged: 4 };
  return aggregated.sort((a, b) => {
    if (a.categoryId === -1) return -1;
    if (b.categoryId === -1) return 1;
    const typeA = a.type ? CAT_TYPE_ORDER.indexOf(a.type) : 999;
    const typeB = b.type ? CAT_TYPE_ORDER.indexOf(b.type) : 999;
    if (typeA !== typeB) return typeA - typeB;
    return order[a.changeType] - order[b.changeType];
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ChangeTypeBadge({ type }: { type: RoleCompareRow['changeType'] }) {
  const config = {
    added:     { label: 'Added',     className: 'bg-accent-success-50 text-accent-success-900 border-accent-success-500' },
    removed:   { label: 'Removed',   className: 'bg-accent-destructive-50 text-accent-destructive-900 border-accent-destructive-500' },
    increased: { label: 'Increased', className: 'bg-accent-warning-50 text-accent-warning-900 border-accent-warning-500' },
    decreased: { label: 'Decreased', className: 'bg-accent-nile-50 text-accent-nile-900 border-accent-nile-500' },
    unchanged: { label: 'Unchanged', className: 'bg-muted text-muted-foreground border-border' },
  }[type];

  return (
    <Badge variant="outline" className={`text-xs ${config.className}`}>
      {config.label}
    </Badge>
  );
}

function DeltaCell({ delta, percent, unit }: { delta: number; percent: number; unit: 'h' | '£' }) {
  if (delta === 0) {
    return (
      <span className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
        <Minus className="h-3 w-3" />
        —
      </span>
    );
  }
  const positive = delta > 0;
  const formatted = unit === '£'
    ? `${positive ? '+' : ''}£${Math.abs(delta).toLocaleString()}`
    : `${positive ? '+' : ''}${delta.toLocaleString()}h`;
  return (
    <span className={`flex items-center justify-end gap-1 text-xs font-medium ${positive ? 'text-accent-warning-900' : 'text-accent-nile-900'}`}>
      {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {formatted}
      <span className="text-muted-foreground">({positive ? '+' : ''}{percent}%)</span>
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function RevisionCompareView({
  revisionA,
  revisionB,
  budgetLayer = 'resource',
  onViewRevA,
  onViewRevB,
  onSwap,
}: RevisionCompareViewProps) {
  const isCostSales = budgetLayer === 'cost' || budgetLayer === 'sales';

  // Resource layer: use roleSnapshot. Cost/sales: use costSnapshot / salesSnapshot.
  const roleSnapshotA = revisionA.resourceSnapshot ?? [];
  const roleSnapshotB = revisionB.resourceSnapshot ?? [];
  const costSalesSnapshotA = (budgetLayer === 'cost' ? revisionA.costSnapshot : revisionA.salesSnapshot) ?? [];
  const costSalesSnapshotB = (budgetLayer === 'cost' ? revisionB.costSnapshot : revisionB.salesSnapshot) ?? [];

  const rows = useMemo(
    () => (isCostSales ? [] : buildCompareRows(roleSnapshotA, roleSnapshotB)),
    [isCostSales, roleSnapshotA, roleSnapshotB]
  );
  const categoryRows = useMemo(
    () => (isCostSales ? buildCategoryCompareRows(costSalesSnapshotA, costSalesSnapshotB) : []),
    [isCostSales, costSalesSnapshotA, costSalesSnapshotB]
  );

  const activeRows = isCostSales ? categoryRows : rows;

  // Totals
  const totalA = isCostSales
    ? costSalesSnapshotA.reduce((sum, c) => sum + c.budget, 0)
    : roleSnapshotA.reduce((sum, r) => sum + r.budgetHours, 0);
  const totalB = isCostSales
    ? costSalesSnapshotB.reduce((sum, c) => sum + c.budget, 0)
    : roleSnapshotB.reduce((sum, r) => sum + r.budgetHours, 0);
  const totalDelta = totalA - totalB;
  const totalPercent = totalB !== 0 ? Math.round((totalDelta / totalB) * 100) : 0;
  const changedCount = activeRows.filter((r) => r.changeType !== 'unchanged').length;
  const hasSnapshots = isCostSales
    ? costSalesSnapshotA.length > 0 || costSalesSnapshotB.length > 0
    : roleSnapshotA.length > 0 || roleSnapshotB.length > 0;

  const unitLabel = isCostSales ? '£' : 'h';
  const rowLabel = isCostSales ? 'Category' : 'Role';
  const deltaLabel = isCostSales ? 'Δ £' : 'Δ Hours';
  const changedLabel = isCostSales ? 'categories' : 'roles';

  if (!hasSnapshots) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground">
            <ArrowLeftRight className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm font-medium">No snapshot data available</p>
            <p className="text-xs mt-1">
              {isCostSales
                ? 'Category-level snapshots were not recorded for these revisions.'
                : 'Role-level snapshots were not recorded for these revisions.'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Column widths — kept consistent across toolbar, header info, column labels, data rows and totals
  const col = {
    role:    'w-[30%] min-w-[140px]',
    revA:    'w-[18%] min-w-[80px]',
    revB:    'w-[18%] min-w-[80px]',
    delta:   'w-[18%] min-w-[100px]',
    change:  'w-[16%] min-w-[90px]',
  };

  return (
    <Card>
      <CardContent className="pt-0 px-0 pb-0 overflow-x-auto">

        {/* ── Toolbar ── */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b bg-muted/20">
          <span className="text-xs text-muted-foreground">
            {changedCount} {changedLabel.slice(0, -1)}{changedCount !== 1 ? changedLabel.slice(-1) : ''} changed
          </span>
          {onSwap && (
            <Button variant="outline" size="sm" onClick={onSwap} className="h-7 text-xs">
              <ArrowLeftRight className="h-3 w-3 mr-1.5" />
              Swap
            </Button>
          )}
        </div>

        {/* ── Column labels ── */}
        <div className="flex border-b bg-muted/60">
          <div className={`${col.role} px-4 py-2.5 shrink-0 text-xs font-medium text-muted-foreground uppercase tracking-wide`}>
            {rowLabel}
          </div>
          <div className={`${col.revA} px-4 py-2.5 shrink-0 text-xs font-medium text-muted-foreground uppercase tracking-wide text-right`}>
            <RevisionColumnHeader revision={revisionA} onView={onViewRevA} unitLabel={unitLabel} />
          </div>
          <div className={`${col.revB} px-4 py-2.5 shrink-0 text-xs font-medium text-muted-foreground uppercase tracking-wide text-right`}>
            <RevisionColumnHeader revision={revisionB} onView={onViewRevB} unitLabel={unitLabel} />
          </div>
          <div className={`${col.delta} px-4 py-2.5 shrink-0 text-xs font-medium text-muted-foreground uppercase tracking-wide text-right`}>
            {deltaLabel}
          </div>
          <div className={`${col.change} px-4 py-2.5 shrink-0 text-xs font-medium text-muted-foreground uppercase tracking-wide text-center`}>
            Change
          </div>
        </div>

        {/* ── Data rows ── */}
        <div className="divide-y">
          {isCostSales
            ? (() => {
                return categoryRows.map((row) => {
                  const isLabourGroup = row.categoryId === -1;
                  const bg = isLabourGroup
                    ? 'bg-muted/40'
                    : row.changeType === 'unchanged'  ? 'opacity-60'
                    : row.changeType === 'added'       ? 'bg-accent-success-50/50'
                    : row.changeType === 'removed'     ? 'bg-accent-destructive-50/50'
                    : row.changeType === 'increased'   ? 'bg-accent-warning-50/60'
                    :                                    'bg-accent-nile-50/40';
                  return (
                    <div key={row.categoryId} className={`flex items-center ${bg}`}>
                      <div className={`${col.role} px-4 py-3 shrink-0 text-sm font-medium`}>
                        <div className="flex items-center gap-1.5">
                          {isLabourGroup && <Lock className="h-3 w-3 text-muted-foreground" />}
                          {row.categoryName}
                        </div>
                      </div>
                      <div className={`${col.revA} px-4 py-3 shrink-0 text-sm text-right tabular-nums`}>
                        {row.revABudget !== null ? `£${row.revABudget.toLocaleString()}` : <span className="text-muted-foreground text-xs">—</span>}
                      </div>
                      <div className={`${col.revB} px-4 py-3 shrink-0 text-sm text-right tabular-nums`}>
                        {row.revBBudget !== null ? `£${row.revBBudget.toLocaleString()}` : <span className="text-muted-foreground text-xs">—</span>}
                      </div>
                      <div className={`${col.delta} px-4 py-3 shrink-0 text-right`}>
                        <DeltaCell delta={row.deltaBudget} percent={row.deltaPercent} unit="£" />
                      </div>
                      <div className={`${col.change} px-4 py-3 shrink-0 text-center`}>
                        <ChangeTypeBadge type={row.changeType} />
                      </div>
                    </div>
                  );
                });
              })()
            : rows.map((row) => {
                const bg =
                  row.changeType === 'unchanged'  ? 'opacity-60' :
                  row.changeType === 'added'       ? 'bg-accent-success-50/50' :
                  row.changeType === 'removed'     ? 'bg-accent-destructive-50/50' :
                  row.changeType === 'increased'   ? 'bg-accent-warning-50/60' :
                                                     'bg-accent-nile-50/40';
                return (
                  <div key={row.roleId} className={`flex items-center ${bg}`}>
                    <div className={`${col.role} px-4 py-3 shrink-0 text-sm font-medium`}>
                      {row.role}
                    </div>
                    <div className={`${col.revA} px-4 py-3 shrink-0 text-sm text-right tabular-nums`}>
                      {row.revAHours !== null ? row.revAHours.toLocaleString() : <span className="text-muted-foreground text-xs">—</span>}
                    </div>
                    <div className={`${col.revB} px-4 py-3 shrink-0 text-sm text-right tabular-nums`}>
                      {row.revBHours !== null ? row.revBHours.toLocaleString() : <span className="text-muted-foreground text-xs">—</span>}
                    </div>
                    <div className={`${col.delta} px-4 py-3 shrink-0 text-right`}>
                      <DeltaCell delta={row.deltaHours} percent={row.deltaPercent} unit="h" />
                    </div>
                    <div className={`${col.change} px-4 py-3 shrink-0 text-center`}>
                      <ChangeTypeBadge type={row.changeType} />
                    </div>
                  </div>
                );
              })}
        </div>

        {/* ── Totals row ── */}
        <div className="flex items-center border-t-2 bg-muted/50 font-semibold">
          <div className={`${col.role} px-4 py-3 shrink-0 text-sm`}>Total</div>
          <div className={`${col.revA} px-4 py-3 shrink-0 text-sm text-right tabular-nums`}>
            {isCostSales ? `£${totalA.toLocaleString()}` : `${totalA.toLocaleString()}h`}
          </div>
          <div className={`${col.revB} px-4 py-3 shrink-0 text-sm text-right tabular-nums`}>
            {isCostSales ? `£${totalB.toLocaleString()}` : `${totalB.toLocaleString()}h`}
          </div>
          <div className={`${col.delta} px-4 py-3 shrink-0 text-right`}>
            <DeltaCell delta={totalDelta} percent={totalPercent} unit={unitLabel} />
          </div>
          <div className={`${col.change} px-4 py-3 shrink-0`} />
        </div>

        {/* ── Footer note (cost/sales only) ── */}
        {isCostSales && (
          <div className="px-4 py-2.5 border-t text-xs text-muted-foreground bg-muted/20">
            For monthly budget distribution, see the Budget &amp; Variance Report.
          </div>
        )}

      </CardContent>
    </Card>
  );
}

// ─── Revision column header with tooltip ─────────────────────────────────────

function RevisionColumnHeader({
  revision,
  onView,
  unitLabel = 'h',
}: {
  revision: BudgetRevision;
  onView?: () => void;
  unitLabel?: 'h' | '£';
}) {
  const date = formatDate(revision.createdAt ?? revision.date);
  const isCurrentOrActive = revision.status === 'current' || revision.status === 'active';
  const isDraft = revision.status === 'draft';
  const author = revision.createdBy ?? revision.changedByName;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center justify-end gap-1 cursor-default group">
          <span className="uppercase tracking-wide">
            {revision.revisionNumber} ({unitLabel})
          </span>
          {isCurrentOrActive && (
            <Badge variant="outline" className="text-xs h-4 bg-accent-success-50 text-accent-success-900 border-accent-success-500 normal-case tracking-normal">
              Current
            </Badge>
          )}
          {isDraft && (
            <Badge variant="outline" className="text-xs h-4 bg-accent-warning-50 text-accent-warning-900 border-accent-warning-500 normal-case tracking-normal">
              Draft
            </Badge>
          )}
          <Info className="h-3 w-3 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-left space-y-2 p-3">
        <div className="text-xs font-semibold">{revision.revisionNumber}</div>
        <div className="text-xs text-muted-foreground">{date}</div>
        {author && <div className="text-xs text-muted-foreground">{author}</div>}
        {onView && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs w-full justify-start -ml-2 mt-1" onClick={onView}>
            <Eye className="h-3 w-3 mr-1" />
            View this revision
          </Button>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
